import { onBeforeUnmount, onMounted, ref } from 'vue';

const getStoredTheme = () => window.localStorage.getItem('theme') || 'light';

/**
 * Module-level so every call site shares one source of truth. It used to be created per call, so
 * a second consumer — the profile menu alongside App.vue — would hold its own copy and show a
 * stale selection after the other one switched the theme.
 */
const theme = ref(getStoredTheme());

export function useTheme() {
  const setTheme = (newTheme) => {
    theme.value = newTheme;
    window.localStorage.setItem('theme', newTheme);

    if (newTheme === 'auto') {
      handleSystemTheme();
    } else {
      applyTheme(newTheme);
      removeSystemThemeListener();
    }
  };

  let systemThemeListener = null;

  const handleSystemTheme = () => {
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)');

    applyTheme(prefersDark.matches ? 'dark' : 'light');

    if (!systemThemeListener) {
      systemThemeListener = (e) => {
        applyTheme(e.matches ? 'dark' : 'light');
      };
      prefersDark.addEventListener('change', systemThemeListener);
    }
  };

  const applyTheme = (theme) => {
    document.documentElement.setAttribute('data-bs-theme', theme);
    document.documentElement.setAttribute('data-mode', theme);
  };

  const removeSystemThemeListener = () => {
    if (systemThemeListener) {
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)');
      prefersDark.removeEventListener('change', systemThemeListener);
      systemThemeListener = null;
    }
  };

  onMounted(() => {
    if (theme.value === 'auto') {
      handleSystemTheme();
    } else {
      applyTheme(theme.value);
    }
  });

  onBeforeUnmount(() => {
    removeSystemThemeListener();
  });

  return {
    theme,
    setTheme,
  };
}
